import {
  Button,
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { TableColWrapper, TableWrapper } from '@common/ui/TableWrapper';
import { TableLoading } from '@common/ui/TableLoading';
import { PagedPagination } from '@common/ui/PagedPagination';
import {
  AccessRequestTemplateClientModel,
  PaginatedClientResponseModelAccessRequestTemplateClientModel,
  useDeleteAccessRequestTemplate,
} from '@client-app-sdk';
import CreationTimeCell from '@common/components/CreationTimeCell';
import IntegrationsCell from '@common/components/IntegrationsCell';
import ResourceTypesCell from '@common/components/ResourceTypesCell';
import { memo, useEffect, useMemo } from 'react';
import ResourcesCell from '@common/components/ResourcesCell';
import { ActionsMenu } from '@common/components/ActionsMenu';
import routes from '@routes';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

type TemplatesTableProps = {
  isLoading: boolean;
  pageData: PaginatedClientResponseModelAccessRequestTemplateClientModel;
  handleOnPageChange: (page: number) => void;
};

const TemplateTableRow = memo(function TemplateTableRow({ template }: { template: AccessRequestTemplateClientModel }) {
  const { mutate: deleteTemplate, isSuccess, isError, isLoading, error } = useDeleteAccessRequestTemplate();

  useEffect(() => {
    if (isSuccess) toast.success('Template deleted successfully');
    if (isError) toast.error(`Something went wrong: ${error}`);
  }, [isSuccess, isError, error]);

  return (
    <TableRow>
      <TableCell component="td">{template.name}</TableCell>
      <TableCell component="td">
        <IntegrationsCell integrations={template.integrations} />
      </TableCell>
      <TableCell component="td">
        <ResourceTypesCell resourceTypes={template.resource_types} />
      </TableCell>
      <TableCell component="td">
        <ResourcesCell resources={template.resources} />
      </TableCell>
      <TableCell component="td">
        <CreationTimeCell creation_time={template.create_date} />
      </TableCell>
      <TableCell component="td">
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={`${routes.NewRequest.path}?template=${template.id}`}
          >
            Use Favorite
          </Button>
          <ActionsMenu onDelete={() => deleteTemplate(template.id)} loading={isLoading} />
        </Stack>
      </TableCell>
    </TableRow>
  );
});

export default function TemplatesTable({
  isLoading,
  pageData: { data, pagination },
  handleOnPageChange,
}: TemplatesTableProps) {
  const rows = useMemo(
    () =>
      data.map((template: AccessRequestTemplateClientModel) => (
        <TemplateTableRow key={template.id} template={template} />
      )),
    [data],
  );

  return (
    <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
      <TableWrapper>
        <TableLoading loading={isLoading} />
        <TableContainer component={Paper} elevation={0}>
          <MuiTable>
            <TableHead>
              <TableRow>
                <TableCell component="th">
                  <TableColWrapper>Name</TableColWrapper>
                </TableCell>
                <TableCell component="th">
                  <TableColWrapper>Integrations</TableColWrapper>
                </TableCell>
                <TableCell component="th">
                  <TableColWrapper>Resource Types</TableColWrapper>
                </TableCell>
                <TableCell component="th">
                  <TableColWrapper>Resources</TableColWrapper>
                </TableCell>
                <TableCell component="th">
                  <TableColWrapper>Created</TableColWrapper>
                </TableCell>
                <TableCell component="th" width="200"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
          </MuiTable>
        </TableContainer>
      </TableWrapper>
      <PagedPagination isLoading={isLoading} pagination={pagination} onChange={handleOnPageChange} />
    </Stack>
  );
}
